import React, { useState } from 'react';
import { Send, Loader2 } from 'lucide-react';

import { usePostComments, useAddComment, type FeedComment } from '../../hooks/student/useFeed';

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d`;
  return new Date(iso).toLocaleDateString([], { month: 'short', day: 'numeric' });
}

function getInitials(first: string | null, last: string | null): string {
  return `${(first || '?')[0]}${(last || '')[0] || ''}`.toUpperCase();
}

function CommentRow({ comment }: { comment: FeedComment }) {
  return (
    <div className="flex gap-2.5 py-2">
      {comment.avatar_url ? (
        <img src={comment.avatar_url} alt="" className="w-7 h-7 rounded-full object-cover shrink-0" />
      ) : (
        <div className="w-7 h-7 rounded-full bg-[#9B1421]/30 flex items-center justify-center text-[10px] font-semibold text-[#EBFFEE] shrink-0">
          {getInitials(comment.first_name, comment.last_name)}
        </div>
      )}
      <div className="min-w-0 flex-1">
        <div className="bg-[#2A2626] rounded-xl px-3 py-2">
          <p className="text-xs font-semibold text-[#EBFFEE]">
            {comment.first_name} {comment.last_name}
          </p>
          <p className="text-sm text-[#EBFFEE]/90 mt-0.5 whitespace-pre-wrap break-words">
            {comment.content}
          </p>
        </div>
        <p className="text-[10px] text-[#A9A3A3] mt-1 ml-3">{timeAgo(comment.created_at)}</p>
      </div>
    </div>
  );
}

interface PostCommentsProps {
  postId: string;
  commentsCount: number;
}

export default function PostComments({ postId, commentsCount }: PostCommentsProps) {
  const [expanded, setExpanded] = useState(false);
  const [text, setText] = useState('');
  const { comments, isLoading } = usePostComments(expanded ? postId : null);
  const addComment = useAddComment();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = text.trim();
    if (!trimmed) return;
    setText('');
    await addComment.mutateAsync({ postId, content: trimmed });
  };

  if (!expanded) {
    if (commentsCount === 0) return null;
    return (
      <button
        onClick={() => setExpanded(true)}
        className="text-xs text-[#A9A3A3] hover:text-[#EBFFEE] transition-colors"
      >
        View {commentsCount === 1 ? '1 comment' : `all ${commentsCount} comments`}
      </button>
    );
  }

  return (
    <div className="mt-1 space-y-1">
      {isLoading ? (
        <div className="flex justify-center py-3">
          <Loader2 className="w-4 h-4 animate-spin text-[#A9A3A3]" />
        </div>
      ) : (
        <>
          {commentsCount > comments.length && (
            <p className="text-[10px] text-[#A9A3A3]">
              Showing {comments.length} of {commentsCount} comments
            </p>
          )}
          {comments.map((c) => (
            <CommentRow key={c.id} comment={c} />
          ))}
        </>
      )}

      {/* Comment input */}
      <form onSubmit={handleSubmit} className="flex items-center gap-2 pt-1">
        <input
          type="text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Write a comment..."
          className="flex-1 bg-[#2A2626] border border-[#504A4A] rounded-full px-3.5 py-2 text-sm text-[#EBFFEE] placeholder-[#6B6565] focus:outline-none focus:border-[#E8535E]/50 transition-colors"
        />
        <button
          type="submit"
          disabled={!text.trim() || addComment.isPending}
          className="p-2 rounded-full bg-[#9B1421] hover:bg-[#7A0F1A] text-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors shrink-0"
        >
          {addComment.isPending ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Send className="w-4 h-4" />
          )}
        </button>
      </form>
    </div>
  );
}
